import { CButton, CCol, CFormCheck, CFormInput, CFormLabel, CFormSelect, CRow } from '@coreui/react';
import React from 'react';
import { useForm } from 'react-hook-form';
import api from 'src/api';
import errorLogger from 'src/api/errorLogger';
import { Title } from 'src/components/atoms/styled/element';
import UploadBusinessLicense from 'src/components/molecules/UploadBusinessLicense';
import UploadGuide from 'src/components/molecules/UploadGuide';
import AdminSearchModal from 'src/components/organisms/AdminSearchModal';
import { Company, CompanyCountry, CompanyFile, CompanyFileRequest, CompanyType } from 'src/types/api/Company';
import { User, UserResponse } from 'src/types/api/User';
import { isAxiosError } from 'src/utils/api/axios';
import MultipartUpload from 'src/utils/MultipartUpload';
import { BusinessType } from 'src/utils/translate';
import { CompanyInfoUpdateModalWrapper } from './styled';

interface FormData {
  companyName: string;
  businessType: CompanyType;
  country: CompanyCountry;
  businessCode: string;
  businessLicense: string;
  representativePhoneNumber: string;
  representativeEmail: string;
  sales: string;
  taxInvoiceEmail?: string;
}

interface Props {
  company?: Company;
  businessLicenseFiles?: CompanyFile[];
  guideFiles?: CompanyFile[];
  salesAccount?: UserResponse;
  callback?: () => void;
}

function CompanyInfoUpdateModal(props: Props) {
  const { company, businessLicenseFiles, guideFiles, salesAccount, callback } = props;

  const { register, setValue, reset, handleSubmit } = useForm<FormData>();

  const [businessLicense, setBusinessLicense] = React.useState<MultipartUpload[]>();
  const [origBusinessLicense, setOrigBusinessLicense] = React.useState<CompanyFile[]>();
  const [origGuide, setOrigGuide] = React.useState<CompanyFile[]>();
  const [guides, setGuides] = React.useState<MultipartUpload[]>();
  const [targetItem, setTargetItem] = React.useState<User>();

  const [visible, setVisible] = React.useState(false);

  const clear = React.useCallback(() => {
    setBusinessLicense(undefined);
    setOrigBusinessLicense(undefined);
    setOrigGuide(undefined);
    setGuides(undefined);
    setTargetItem(undefined);
    setVisible(false);

    return reset({});
  }, [reset]);

  const handleConfirm = React.useCallback(() => {
    window.alert('정보 변경이 완료되었습니다.');

    return clear();
  }, [clear]);

  const submit = React.useCallback(
    async (data: FormData) => {
      const companyId = company?.id;
      if (!company || !companyId) {
        errorLogger.error('Company does not exist');

        return window.alert('회사 정보가 존재하지 않습니다.');
      }

      const {
        businessType,
        companyName,
        country,
        businessCode,
        representativePhoneNumber,
        representativeEmail,
        taxInvoiceEmail,
      } = data;

      const request: Company = {
        ...company,
        name: companyName ? companyName : company.name,
        companyType: businessType,
        country: country,
        businessRegistrationNumber: businessCode,
        phoneNumber: representativePhoneNumber,
        email: representativeEmail,
        managerAccountId: targetItem?.id,
        taxInvoiceEmail: taxInvoiceEmail,
      };
      try {
        const companyRes = await api.company.item(companyId).put(request);
        if (isAxiosError(companyRes)) {
          throw companyRes;
        }

        const businessLicenseFiles = (businessLicense
          ?.map((license) => {
            if (!license.fileId) {
              return undefined;
            }

            const businessLicenseFile = {
              companyId: companyId,
              fileId: license.fileId,
              fileType: 'BUSINESS_LICENSE',
            };

            return businessLicenseFile;
          })
          .filter(Boolean) ?? []) as CompanyFileRequest[];

        const businessGuideFiles = (guides
          ?.map((guide) => {
            if (!guide.fileId) {
              return undefined;
            }

            const businessGuideFile = {
              companyId: companyId,
              fileId: guide.fileId,
              fileType: 'GUIDE',
            };

            return businessGuideFile;
          })
          .filter(Boolean) ?? []) as CompanyFileRequest[];

        const files = [...businessGuideFiles, ...businessLicenseFiles];

        if (origBusinessLicense && origBusinessLicense.length > 0) {
          origBusinessLicense.forEach((it) =>
            files.concat({ companyId: companyId, fileId: it.fileId, fileType: it.fileType })
          );
        }
        if (origGuide && origGuide.length > 0) {
          origGuide.forEach((it) => files.concat({ companyId: companyId, fileId: it.fileId, fileType: it.fileType }));
        }
        if (files.length !== 0) {
          const fileRes = await api.company.item(companyId).files.post(files);
          if (isAxiosError(fileRes)) {
            throw fileRes;
          }
        }

        return handleConfirm();
      } catch (error) {
        errorLogger.error(error);

        return window.alert('회사 정보 수정이 실패하였습니다.');
      } finally {
        callback?.();
      }
    },
    [company, targetItem?.id, businessLicense, guides, origBusinessLicense, origGuide, handleConfirm, callback]
  );

  const salesTargetHandler = React.useCallback(
    (data: User) => {
      setValue('sales', data.name);
      setTargetItem(data);
    },
    [setValue]
  );

  const handleDismiss = React.useCallback(() => {
    clear();
    callback?.();
    setVisible(false);
  }, [clear, callback, setVisible]);

  React.useEffect(() => {
    if (company) {
      const companyData: Partial<FormData> = {
        companyName: company.name,
        businessType: company.companyType,
        country: company.country,
        representativeEmail: company.email || '',
        businessCode: company.businessRegistrationNumber || '',
        representativePhoneNumber: company.phoneNumber || '',
        taxInvoiceEmail: company.taxInvoiceEmail,
      };
      reset(companyData);
    }

    if (businessLicenseFiles) {
      setOrigBusinessLicense(businessLicenseFiles);
    }
    if (guideFiles) {
      setOrigGuide(guideFiles);
    }
    if (salesAccount) {
      setValue('sales', salesAccount.name);
      setTargetItem(salesAccount);
    }
  }, [company, businessLicenseFiles, guideFiles, salesAccount, reset, setValue]);

  return (
    <div className="align-items-center d-flex">
      <CButton style={{ width: 'max-content' }} onClick={() => setVisible(!visible)}>
        수정하기
      </CButton>
      <CompanyInfoUpdateModalWrapper
        title={'회사정보 수정하기'}
        visible={visible}
        onDismiss={() => handleDismiss()}
        onSubmit={handleSubmit(submit)}
      >
        <Title>회사정보</Title>
        <CRow className="align-items-center">
          <CCol>
            <CFormLabel>회사명</CFormLabel>
          </CCol>
          <CCol sm={8} style={{ position: 'relative' }}>
            <CFormInput type={'text'} {...register('companyName', { required: true })} />
          </CCol>
        </CRow>

        <CRow>
          <CCol>
            <CFormLabel>구분</CFormLabel>
          </CCol>

          <CCol sm={8}>
            <CRow>
              {Object.keys(BusinessType).map((it) => (
                <CCol key={it}>
                  <CFormCheck
                    type="radio"
                    {...register('businessType', { required: true })}
                    value={it}
                    label={BusinessType[it]}
                  />
                </CCol>
              ))}
            </CRow>
          </CCol>
        </CRow>
        <CRow>
          <CCol>
            <CFormLabel>국가</CFormLabel>
          </CCol>

          <CCol sm={8}>
            <CFormSelect {...register('country', { required: true })}>
              <option value={'KOR'}>KOR</option>
              <option value={'JPN'}>JPN</option>
              <option value={'ENG'}>ENG</option>
            </CFormSelect>
          </CCol>
        </CRow>

        <CRow className="align-items-center">
          <CCol>
            <CFormLabel>사업자 등록번호</CFormLabel>
          </CCol>
          <CCol sm={8} style={{ position: 'relative' }}>
            <CFormInput type={'text'} {...register('businessCode', { required: true })} />
          </CCol>
        </CRow>

        <CRow className="align-items-center">
          <CCol>
            <CFormLabel>사업자 등록증</CFormLabel>
          </CCol>
          <CCol sm={8} style={{ position: 'relative' }}>
            <UploadBusinessLicense
              setFile={(file) => setBusinessLicense(file)}
              origFile={origBusinessLicense}
              setOrigFile={(file) => setOrigBusinessLicense(file)}
            />
          </CCol>
        </CRow>
        <CRow className="align-items-center">
          <CCol>
            <CFormLabel>대표 전화번호</CFormLabel>
          </CCol>
          <CCol sm={8} style={{ position: 'relative' }}>
            <CFormInput type={'text'} {...register('representativePhoneNumber')} />
          </CCol>
        </CRow>

        <CRow className="align-items-center">
          <CCol>
            <CFormLabel>대표 이메일</CFormLabel>
          </CCol>
          <CCol sm={8} style={{ position: 'relative' }}>
            <CFormInput type={'text'} {...register('representativeEmail', { required: true })} />
          </CCol>
        </CRow>
        <CRow className="align-items-center">
          <CCol>
            <CFormLabel>세금계산서 담당 이메일</CFormLabel>
          </CCol>
          <CCol sm={8} style={{ position: 'relative' }}>
            <CFormInput type={'text'} {...register('taxInvoiceEmail')} />
          </CCol>
        </CRow>
        <CRow className="align-items-center">
          <CCol>
            <CFormLabel>대표 작업 가이드</CFormLabel>
          </CCol>
          <CCol sm={8} style={{ position: 'relative' }}>
            <UploadGuide
              setFile={(file) => setGuides(file)}
              origFile={origGuide}
              setOrigFile={(file) => setOrigGuide(file)}
            />
          </CCol>
        </CRow>
        <CRow className="align-items-center">
          <CCol>
            <CFormLabel>담당 sales</CFormLabel>
          </CCol>
          <CCol sm={8} style={{ position: 'relative' }}>
            <CFormInput type={'text'} {...register('sales', { required: true })} disabled={true} />
            <div
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
              }}
            >
              <AdminSearchModal setTargetItem={(item: User) => salesTargetHandler(item)} />
            </div>
          </CCol>
        </CRow>
      </CompanyInfoUpdateModalWrapper>
    </div>
  );
}

export default CompanyInfoUpdateModal;
